import { Fragment, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getFormateurs,
  deleteFormateur,
  updateFormateur,
  creerAccesFormateur,
  reinitialiserAccesFormateur,
} from '../services/formateurService';
import FormateurFormulaire from './FormateurFormulaire';
import { formatDate, formaterTelephone } from './devisUtils';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/DevisPanel.css';
import '../assets/styles/Devis.css';
import '../assets/styles/Clients.css';
import '../assets/styles/Parametrage.css';
import { AlerteAttestations, PIECES_ATTENDUES, attestationsEchues, etatEcheance } from './PiecesFormateur';

const ATTESTATIONS = [
  { type: 'urssaf', libelle: 'URSSAF' },
  { type: 'rc_pro', libelle: 'RC Pro' },
];

/**
 * Les attestations à signaler sur la ligne : manquante, échue, ou proche de
 * l'échéance (30 jours). Une attestation à jour ne dit rien.
 */
function alertesAttestation(attestations = []) {
  const alertes = [];
  for (const { type, libelle } of ATTESTATIONS) {
    const piece = (attestations || []).find((a) => a.type === type);
    if (!piece) {
      alertes.push({ type, libelle: `${libelle} manquante`, classe: '', titre: `Aucune ${libelle} au dossier` });
      continue;
    }
    if (!piece.echeance) {
      alertes.push({ type, libelle: `${libelle} : échéance à compléter`, classe: ' devis-pill-attente', titre: 'Date d’échéance non renseignée' });
      continue;
    }
    // Échéance atteinte : c'est le bandeau rouge de la ligne qui le dit.
    if (attestationsEchues(attestations).some((a) => a.type === type)) continue;
    const etat = etatEcheance(piece.echeance);
    if (etat && etat.classe !== ' devis-pill-valide') {
      alertes.push({ type, libelle: `${libelle} : ${etat.libelle}`, classe: etat.classe, titre: etat.libelle });
    }
  }
  return alertes;
}

/* Même pastille à initiales que la liste des clients : un formateur est un
   contact comme un autre, on le reconnaît de la même façon. */
const TEINTES = [8, 32, 96, 150, 190, 215, 260, 290, 330];

function couleurAvatar(email) {
  const texte = String(email || '');
  let somme = 0;
  for (let i = 0; i < texte.length; i += 1) somme = (somme + texte.charCodeAt(i) * (i + 1)) % 9973;
  return TEINTES[somme % TEINTES.length];
}

function initiales(f) {
  return (((f.prenom || '')[0] || '') + ((f.nom || '')[0] || '')).toUpperCase() || '?';
}

/** Code du formateur : ses initiales suivies de son numéro de fiche (CM12). */
export function codeFormateur(f) {
  return `${initiales(f)}${f.id}`;
}

export function AvatarFormateur({ formateur }) {
  const teinte = couleurAvatar(formateur.email);
  return (
    <span
      className="clients-avatar"
      style={{ background: `hsl(${teinte} 42% 92%)`, color: `hsl(${teinte} 52% 30%)` }}
      aria-hidden="true"
    >
      {initiales(formateur)}
    </span>
  );
}

/**
 * Menu Formateurs : la liste des fiches. Une fiche s'assigne à une journée
 * depuis la planification ; ici on la crée, on la corrige, et on ouvre son
 * dossier (journées, documents, émargements).
 */
function Formateurs() {
  const navigate = useNavigate();
  const [formateurs, setFormateurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [saisie, setSaisie] = useState('');
  const [recherche, setRecherche] = useState('');

  // null = modale fermée ; { id: null } = création ; { id: 12 } = édition
  const [edition, setEdition] = useState(null);
  const [suppression, setSuppression] = useState(null);
  // Fiche qu'on s'apprête à désactiver (la réactivation se fait d'un clic).
  const [desactivation, setDesactivation] = useState(null);
  const [travail, setTravail] = useState(false);
  const [flash, setFlash] = useState(null);
  // Fiche dont l'accès est en cours de création / réinitialisation.
  const [accesEnCours, setAccesEnCours] = useState(null);
  // Les mots de passe affichés en clair (masqués par défaut), par fiche.
  const [mdpVisibles, setMdpVisibles] = useState(() => new Set());

  useEffect(() => {
    const t = setTimeout(() => setRecherche(saisie), 300);
    return () => clearTimeout(t);
  }, [saisie]);

  const charger = useCallback(() => {
    setLoading(true);
    setError(null);
    return getFormateurs({ search: recherche })
      .then((res) => setFormateurs(res.data || []))
      .catch((err) => setError(err.message || 'Impossible de charger les formateurs.'))
      .finally(() => setLoading(false));
  }, [recherche]);

  useEffect(() => {
    charger();
  }, [charger]);

  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), 3500);
    return () => clearTimeout(t);
  }, [flash]);

  /** Crée le compte de connexion du formateur (mot de passe par défaut). */
  async function creerAcces(f) {
    setAccesEnCours(f.id);
    try {
      const res = await creerAccesFormateur(f.id);
      setFlash(res.message || 'Accès créé.');
      await charger();
    } catch (err) {
      setFlash(err.message || 'Création de l’accès impossible.');
    } finally {
      setAccesEnCours(null);
    }
  }

  /** Remet le mot de passe par défaut : à utiliser quand il est oublié. */
  async function reinitialiserAcces(f) {
    if (
      !window.confirm(
        `Remettre le mot de passe par défaut de ${f.prenom} ${f.nom} ? Ses sessions ouvertes seront fermées.`
      )
    ) {
      return;
    }
    setAccesEnCours(f.id);
    try {
      const res = await reinitialiserAccesFormateur(f.id);
      setFlash(res.message || 'Mot de passe réinitialisé.');
    } catch (err) {
      setFlash(err.message || 'Réinitialisation impossible.');
    } finally {
      setAccesEnCours(null);
    }
  }

  /** Désactive ou réactive la fiche : le compte de connexion suit, la fiche reste. */
  async function changerEtat(f, actif) {
    setTravail(true);
    try {
      await updateFormateur(f.id, { actif });
      setFlash(
        actif
          ? `${f.prenom} ${f.nom} est réactivé : il peut de nouveau être assigné et se connecter.`
          : `${f.prenom} ${f.nom} est désactivé : plus d’assignation, accès fermé.`
      );
      setDesactivation(null);
      await charger();
    } catch (err) {
      setFlash(err.message || 'Changement impossible.');
    } finally {
      setTravail(false);
    }
  }

  async function supprimer() {
    if (!suppression) return;
    setTravail(true);
    try {
      const res = await deleteFormateur(suppression.id);
      setFlash(res.message || 'Fiche supprimée (corbeille).');
      setSuppression(null);
      await charger();
    } catch (err) {
      setFlash(err.message || 'Suppression impossible.');
    } finally {
      setTravail(false);
    }
  }

  const enEdition = edition && edition.id !== null ? formateurs.find((f) => f.id === edition.id) : null;

  return (
    <div className="devis-page">
      <div className="devis-header">
        <div>
          <h1 className="devis-title">Formateurs</h1>
          <p className="devis-subtitle">
            Les personnes qui animent les formations.
          </p>
        </div>
        <div className="devis-header-actions">
          <button
            type="button"
            className="devis-btn devis-btn-primary"
            onClick={() => setEdition({ id: null })}
          >
            + Ajouter un formateur
          </button>
        </div>
      </div>

      <div className="devis-toolbar">
        <input
          type="search"
          className="devis-search"
          placeholder="Rechercher un formateur (nom, email, fonction)"
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
        />
        {flash && <span className="param-flash">{flash}</span>}
      </div>

      <div className="devis-card">
        {loading && <div className="devis-state">Chargement...</div>}
        {!loading && error && <div className="devis-state devis-state-error">{error}</div>}

        {!loading && !error && formateurs.length === 0 && (
          <div className="devis-state">
            {recherche ? 'Aucun formateur ne correspond.' : 'Aucun formateur enregistré.'}
          </div>
        )}

        {!loading && !error && formateurs.length > 0 && (
          <div className="devis-table-wrap">
          <table className="devis-table devis-table-formateurs">
            <thead>
              <tr>
                <th>FORMATEUR · FONCTION</th>
                <th>JOURNÉES</th>
                <th>ÉTAT</th>
                <th className="devis-th-actions">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {formateurs.map((f) => {
                const echues = f.actif ? attestationsEchues(f.attestations) : [];
                const manquantes = PIECES_ATTENDUES.filter((p) => !(f.piecesTypes || []).includes(p.type)).map(
                  (p) => p.libelle
                );
                return (
                <Fragment key={f.id}>
                {/* Échéance atteinte : un bandeau sur toute la largeur de la
                    ligne. En cartes (écran étroit), il passe dans la carte. */}
                {echues.length > 0 && (
                  <tr
                    className="devis-row formateur-ligne-alerte formateur-bandeau-ligne"
                    onClick={() => navigate(`/formateurs/${f.id}`)}
                  >
                    <td colSpan={4}>
                      <AlerteAttestations echues={echues} />
                    </td>
                  </tr>
                )}
                <tr
                  className={`devis-row${echues.length ? ' formateur-ligne-alerte' : ''}`}
                  onClick={() => navigate(`/formateurs/${f.id}`)}
                >
                  <td>
                    <div className="formateur-alerte-cellule">
                      <AlerteAttestations echues={echues} />
                    </div>
                    <div className="clients-identite">
                      <AvatarFormateur formateur={f} />
                      <div className="clients-identite-texte">
                        <div className="clients-nom">
                          {/* Le code identifie la fiche partout (CV anonyme compris). */}
                          <span className="formateur-code">{codeFormateur(f)}</span>
                          {[f.civilite, f.prenom, f.nom].filter(Boolean).join(' ')}
                          {/* Un petit signal rouge tant qu'il manque des pièces au dossier. */}
                          {f.actif && manquantes.length > 0 && (
                            <span
                              className="formateur-manque"
                              role="img"
                              title={`Pièces manquantes : ${manquantes.join(', ')}`}
                              aria-label={`Pièces manquantes : ${manquantes.join(', ')}`}
                            >
                              !
                            </span>
                          )}
                        </div>
                        <div className="clients-email">{f.email}</div>
                        <div className="clients-tel">{formaterTelephone(f.telephone) || '—'}</div>
                        <div className="devis-status-detail">{f.fonction || '—'}</div>
                        {/* L'identifiant de connexion du formateur (accès à son espace). */}
                        {f.acces && (
                          <div className="devis-status-detail" title="Identifiant de connexion">
                            Accès : {f.acces.identifiant}
                            {f.acces.actif ? '' : ' (désactivé)'}
                          </div>
                        )}
                        {/* Le mot de passe : masqué tant qu'on ne le demande pas. Seul le
                            mot de passe par défaut est connu ; un mot de passe changé par
                            le formateur ne se lit jamais (l'application n'en garde qu'une empreinte). */}
                        {f.acces && (
                          <div className="formateur-mdp" title="Mot de passe de connexion">
                            Mot de passe :{' '}
                            {f.acces.motDePasseChange ? (
                              <span className="formateur-mdp-change">changé par le formateur (non consultable)</span>
                            ) : (
                              <>
                                <code className="formateur-mdp-valeur">
                                  {mdpVisibles.has(f.id) ? f.acces.motDePasse : '••••••••••'}
                                </code>
                                <button
                                  type="button"
                                  className="formateur-mdp-bouton"
                                  onClick={() =>
                                    setMdpVisibles((prec) => {
                                      const suite = new Set(prec);
                                      if (suite.has(f.id)) suite.delete(f.id);
                                      else suite.add(f.id);
                                      return suite;
                                    })
                                  }
                                >
                                  {mdpVisibles.has(f.id) ? 'Masquer' : 'Afficher'}
                                </button>
                                <button
                                  type="button"
                                  className="formateur-mdp-bouton"
                                  onClick={() => navigator.clipboard?.writeText(f.acces.motDePasse).catch(() => {})}
                                >
                                  Copier
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td data-label="JOURNÉES">
                    {f.nbSessions > 0 ? (
                      <>
                        <span className="devis-status-pill">
                          {f.nbSessions} journée{f.nbSessions > 1 ? 's' : ''}
                        </span>
                        <div className="devis-status-detail">
                          {f.prochaineJournee
                            ? `prochaine le ${formatDate(f.prochaineJournee)}`
                            : 'aucune à venir'}
                        </div>
                      </>
                    ) : (
                      <span className="devis-status-detail">aucune</span>
                    )}
                  </td>
                  <td data-label="ÉTAT">
                    <span className={`devis-status-pill${f.actif ? ' devis-pill-valide' : ''}`}>
                      {f.actif ? 'Actif' : 'Retiré'}
                    </span>
                    {/* Attestations URSSAF / RC Pro : ce qui manque ou arrive à
                        échéance se voit dès la liste (migration 080). */}
                    {f.actif &&
                      alertesAttestation(f.attestations).map((a) => (
                        <div key={a.type} style={{ marginTop: 4 }}>
                          <span className={`devis-status-pill${a.classe}`} title={a.titre}>
                            {a.libelle}
                          </span>
                        </div>
                      ))}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="devis-actions-pile">
                      <button
                        type="button"
                        className="devis-btn devis-btn-sm devis-btn-primary"
                        onClick={() => navigate(`/formateurs/${f.id}`)}
                      >
                        Fiche
                      </button>
                      <button
                        type="button"
                        className="devis-btn devis-btn-sm"
                        onClick={() => setEdition({ id: f.id })}
                      >
                        Modifier
                      </button>
                      {/* Désactiver : la fiche n'est plus proposée à l'assignation et son accès
                          se ferme ; elle reste consultable et se réactive d'un clic. */}
                      <button
                        type="button"
                        className="devis-btn devis-btn-sm"
                        onClick={() => (f.actif ? setDesactivation(f) : changerEtat(f, true))}
                        disabled={travail}
                        title={f.actif ? 'Ne plus proposer ce formateur et fermer son accès' : 'Proposer de nouveau ce formateur et rouvrir son accès'}
                      >
                        {f.actif ? 'Désactiver' : 'Réactiver'}
                      </button>
                      {/* Pas encore d'accès : le créer d'un clic. Sinon, le
                          mot de passe oublié se remet à sa valeur par défaut. */}
                      {f.acces ? (
                        <button
                          type="button"
                          className="devis-btn devis-btn-sm"
                          onClick={() => reinitialiserAcces(f)}
                          disabled={accesEnCours === f.id}
                          title="Remettre le mot de passe par défaut"
                        >
                          {accesEnCours === f.id ? 'En cours' : 'Réinit. accès'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="devis-btn devis-btn-sm devis-btn-primary"
                          onClick={() => creerAcces(f)}
                          disabled={accesEnCours === f.id}
                          title="Créer l'accès du formateur à son espace"
                        >
                          {accesEnCours === f.id ? 'Création' : 'Créer l’accès'}
                        </button>
                      )}
                      <button
                        type="button"
                        className="devis-btn devis-btn-sm"
                        onClick={() => setSuppression(f)}
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
                </Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {edition && (
        <FormateurFormulaire
          formateur={enEdition}
          onClose={() => setEdition(null)}
          onEnregistre={(fiche) => {
            setEdition(null);
            setFlash(
              enEdition
                ? 'Fiche enregistrée.'
                : `${fiche.prenom} ${fiche.nom} ajouté(e)${
                    fiche.acces
                      ? ` — accès créé (identifiant ${fiche.acces.identifiant}).`
                      : fiche.accesErreur
                        ? ` — accès non créé : ${fiche.accesErreur}`
                        : '.'
                  }`
            );
            charger();
          }}
        />
      )}

      {desactivation && (
        <div className="param-modal-overlay" onClick={() => setDesactivation(null)}>
          <div className="param-modal param-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="param-modal-header">
              <h2 className="param-modal-title">Désactiver ce formateur</h2>
            </div>
            <div className="param-modal-body">
              <p className="param-confirm-text">
                Désactiver{' '}
                <strong>
                  {desactivation.prenom} {desactivation.nom}
                </strong>{' '}
                ? Il ne sera plus proposé à l’assignation et son accès à l’espace formateur sera fermé (ses sessions
                ouvertes aussi).
              </p>
              <p className="param-confirm-text">
                Rien n’est supprimé : la fiche, ses pièces et ses journées restent en place, et le bouton
                « Réactiver » le remet en service.
              </p>
              <div className="param-form-actions">
                <button type="button" className="param-btn" onClick={() => setDesactivation(null)} disabled={travail}>
                  Annuler
                </button>
                <button
                  type="button"
                  className="param-btn param-btn-danger-solid"
                  onClick={() => changerEtat(desactivation, false)}
                  disabled={travail}
                >
                  {travail ? 'En cours' : 'Désactiver'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {suppression && (
        <div className="param-modal-overlay" onClick={() => setSuppression(null)}>
          <div className="param-modal param-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="param-modal-header">
              <h2 className="param-modal-title">Supprimer la fiche</h2>
            </div>
            <div className="param-modal-body">
              <p className="param-confirm-text">
                Supprimer{' '}
                <strong>
                  {suppression.prenom} {suppression.nom}
                </strong>
                ? Elle ne sera plus assignable ; la fiche reste restaurable depuis la corbeille.
              </p>
              {Number(suppression.nbSessions) > 0 && (
                <p className="param-confirm-text">
                  Elle est assignée à <strong>{suppression.nbSessions}</strong> journée
                  {Number(suppression.nbSessions) > 1 ? 's' : ''} : {Number(suppression.nbSessions) > 1 ? 'elles perdent' : 'elle perd'} le
                  lien vers la fiche, mais son nom y reste écrit. Si cette personne n’est pas le formateur
                  (un participant pris pour tel, par exemple), corrigez ensuite la planification de ces journées.
                </p>
              )}
              <div className="param-form-actions">
                <button
                  type="button"
                  className="param-btn"
                  onClick={() => setSuppression(null)}
                  disabled={travail}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="param-btn param-btn-danger-solid"
                  onClick={supprimer}
                  disabled={travail}
                >
                  {travail ? 'Suppression' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Formateurs;
